import type { ContractType } from "./watchTypes";

export function parseOptionContractDetails(optionContract?: string | null) {
  const normalized = optionContract?.trim();
  if (!normalized) return null;

  const match = normalized.match(/^O:([A-Z]+)(\d{6})([CP])(\d{8})$/i);
  if (!match) return null;

  const [, contractTicker, dateStr, contractSide, strikeRaw] = match;
  const yy = dateStr.slice(0, 2);
  const mm = dateStr.slice(2, 4);
  const dd = dateStr.slice(4, 6);
  const strike = (Number(strikeRaw) / 1000).toString();

  return {
    ticker: contractTicker.toUpperCase(),
    expiry: `20${yy}-${mm}-${dd}`,
    strike,
    type: contractSide.toUpperCase() === "P" ? "put" : "call",
  } as const;
}

export function getDteFromContractExpiry(contractExpiry: string) {
  const expireParts = contractExpiry.split("-");
  if (expireParts.length !== 3) return null;

  const expiryDate = new Date(
    parseInt(expireParts[0], 10),
    parseInt(expireParts[1], 10) - 1,
    parseInt(expireParts[2], 10),
  );
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffTime = expiryDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function getDteFromManualOcc(occ: string) {
  const match = occ.trim().toUpperCase().match(/(\d{6})[CP]\d{8}$/);
  if (!match) return null;

  const dateStr = match[1];
  const yy = parseInt(dateStr.slice(0, 2), 10);
  const mm = parseInt(dateStr.slice(2, 4), 10) - 1;
  const dd = parseInt(dateStr.slice(4, 6), 10);
  const expiryDate = new Date(2000 + yy, mm, dd);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffTime = expiryDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function buildOccContract(params: {
  ticker: string;
  contractExpiry: string;
  contractStrike: string;
  contractType: ContractType;
}) {
  const yy = params.contractExpiry.slice(2, 4);
  const mm = params.contractExpiry.slice(5, 7);
  const dd = params.contractExpiry.slice(8, 10);
  const strikePadded = String(Math.round(parseFloat(params.contractStrike) * 1000)).padStart(8, "0");
  if (!yy || !mm || !dd || !strikePadded) {
    throw new Error("Invalid contract fields");
  }
  return `O:${params.ticker.toUpperCase()}${yy}${mm}${dd}${params.contractType === "put" ? "P" : "C"}${strikePadded}`;
}

export function pickPreferredStrike(
  strikes: string[],
  spot: number | null | undefined,
) {
  if (!strikes.length) return "";

  const normalized = strikes
    .map((raw) => ({ raw, value: Number(raw) }))
    .filter((item) => Number.isFinite(item.value));
  if (!normalized.length) return strikes[0];

  if (!spot || spot <= 0) return normalized[0].raw;

  const atOrAbove = normalized.filter((item) => item.value >= spot);
  if (atOrAbove.length > 0) {
    return atOrAbove.reduce((prev, curr) =>
      curr.value < prev.value ? curr : prev,
    ).raw;
  }

  return normalized.reduce((prev, curr) =>
    Math.abs(curr.value - spot) < Math.abs(prev.value - spot) ? curr : prev,
  ).raw;
}
